import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import {
  FileText, Users, CreditCard, Receipt, AlertCircle,
  Plus, ArrowRight, TrendingUp,
} from 'lucide-react'
import { getDashboard } from '../services/api'
import { formatCurrency, formatDate } from '../lib/formatters'
import StatusBadge from '../components/ui/StatusBadge'
import Avatar from '../components/ui/Avatar'
import { HintBanner } from '../components/ui/Tooltip'
import { useUserMode } from '../contexts/UserModeContext'

export default function Dashboard() {
  const [data, setData] = useState(null)
  const [loading, setLoading] = useState(true)
  const { isSimple, dismissHint, isHintDismissed } = useUserMode()

  useEffect(() => {
    getDashboard()
      .then((res) => setData(res.data))
      .catch(() => {})
      .finally(() => setLoading(false))
  }, [])

  if (loading) {
    return (
      <div className="space-y-6">
        <h1 className="text-2xl font-semibold text-gray-900">Dashboard</h1>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {[...Array(4)].map((_, i) => (
            <div key={i} className="card animate-pulse h-28" />
          ))}
        </div>
      </div>
    )
  }

  const stats = data || {}
  const recentInvoices = stats.recentInvoices || []
  const needsAttention = recentInvoices.filter(
    (inv) => inv.status === 'DRAFT' || inv.status === 'OVERDUE'
  )

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-semibold text-gray-900">Dashboard</h1>
        {!isSimple && (
          <span className="text-xs text-gray-400">
            {new Date().toLocaleDateString('en-IN', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })}
          </span>
        )}
      </div>

      {/* First-time hint */}
      <HintBanner id="dashboard-welcome" dismissed={isHintDismissed('dashboard-welcome')} onDismiss={dismissHint}>
        Welcome to Osteo3d Books! Start by adding a customer, then create your first invoice. Use the quick actions below to get going.
      </HintBanner>

      {/* Stat cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard
          icon={FileText} label="Outstanding" color="brand"
          value={formatCurrency(stats.totalOutstanding || 0)}
          to="/invoices?status=SENT&status=PARTIAL"
        />
        <StatCard
          icon={AlertCircle} label="Overdue" color="red"
          value={formatCurrency(stats.totalOverdue || 0)}
          to="/invoices?status=OVERDUE"
          pulse={stats.totalOverdue > 0}
        />
        <StatCard
          icon={TrendingUp} label="Revenue (This Month)" color="green"
          value={formatCurrency(stats.monthlyRevenue || 0)}
        />
        {!isSimple && (
          <StatCard
            icon={Receipt} label="Pending Expenses" color="amber"
            value={formatCurrency(stats.pendingExpenses || 0)}
          />
        )}
      </div>

      {/* Quick Actions */}
      <div>
        <h2 className="text-sm font-medium text-gray-500 uppercase tracking-wider mb-3">Quick Actions</h2>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <QuickAction to="/invoices/new" icon={Plus} color="brand" label="Create Invoice" desc="Bill a customer" />
          <QuickAction to="/payments" icon={CreditCard} color="emerald" label="Record Payment" desc="Log a payment received" />
          <QuickAction to="/customers/new" icon={Users} color="violet" label="Add Customer" desc="New customer record" />
        </div>
      </div>

      {/* Needs Your Attention */}
      {needsAttention.length > 0 && (
        <div className="card border-l-4 border-l-amber-400">
          <h2 className="text-base font-semibold text-gray-900 mb-3 flex items-center gap-2">
            <AlertCircle className="h-4 w-4 text-amber-500" />
            Needs Your Attention
          </h2>
          <div className="space-y-2">
            {needsAttention.map((inv) => (
              <Link
                key={inv.id}
                to={`/invoices/${inv.id}`}
                className="flex items-center justify-between rounded-lg px-3 py-2 hover:bg-gray-50 transition-colors group"
              >
                <div className="flex items-center gap-3">
                  <Avatar name={inv.customer?.name} />
                  <div>
                    <span className="text-sm font-medium text-gray-900 group-hover:text-brand-600">
                      {inv.number || 'DRAFT'}
                    </span>
                    <span className="text-xs text-gray-500 ml-2">{inv.customer?.name}</span>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <span className="text-sm font-medium text-gray-700">{formatCurrency(inv.balanceDue ?? inv.total, inv.currency)}</span>
                  <StatusBadge status={inv.status} />
                  <ArrowRight className="h-3.5 w-3.5 text-gray-300 group-hover:text-brand-500 transition-colors" />
                </div>
              </Link>
            ))}
          </div>
        </div>
      )}

      {/* Recent Invoices */}
      <div className="card">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-medium text-gray-900">Recent Invoices</h2>
          <Link to="/invoices" className="text-sm text-brand-600 hover:text-brand-700 inline-flex items-center gap-1">
            View all <ArrowRight className="h-3.5 w-3.5" />
          </Link>
        </div>
        {recentInvoices.length > 0 ? (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-gray-500 border-b">
                  <th className="pb-2 font-medium">Invoice</th>
                  <th className="pb-2 font-medium">Customer</th>
                  <th className="pb-2 font-medium">Amount</th>
                  <th className="pb-2 font-medium">Status</th>
                  {!isSimple && <th className="pb-2 font-medium">Date</th>}
                </tr>
              </thead>
              <tbody>
                {recentInvoices.map((inv) => (
                  <tr key={inv.id} className="border-b last:border-0 hover:bg-gray-50/50 transition-colors">
                    <td className="py-2.5">
                      <Link to={`/invoices/${inv.id}`} className="text-brand-600 hover:text-brand-700 font-medium">
                        {inv.number || 'DRAFT'}
                      </Link>
                    </td>
                    <td className="py-2.5">
                      <div className="flex items-center gap-2">
                        <Avatar name={inv.customer?.name} />
                        <span className="text-gray-700">{inv.customer?.name || '\u2014'}</span>
                      </div>
                    </td>
                    <td className="py-2.5 font-medium">{formatCurrency(inv.total, inv.currency)}</td>
                    <td className="py-2.5">
                      <StatusBadge status={inv.status} />
                    </td>
                    {!isSimple && <td className="py-2.5 text-gray-500">{formatDate(inv.date)}</td>}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="text-center py-8">
            <FileText className="h-10 w-10 text-gray-300 mx-auto mb-2" />
            <p className="text-sm text-gray-500">No invoices yet. Create your first one!</p>
            <Link to="/invoices/new" className="text-sm text-brand-600 hover:text-brand-700 font-medium mt-1 inline-block">
              Create Invoice
            </Link>
          </div>
        )}
      </div>
    </div>
  )
}

/* ---- Sub-components ---- */

function StatCard({ icon: Icon, label, value, color, to, pulse }) {
  const palettes = {
    brand:  'bg-brand-50 text-brand-600',
    red:    'bg-red-50 text-red-600',
    green:  'bg-emerald-50 text-emerald-600',
    amber:  'bg-amber-50 text-amber-600',
  }

  const inner = (
    <div className={`card flex items-center gap-4 transition-shadow ${to ? 'hover:shadow-md cursor-pointer group' : ''}`}>
      <div className={`p-2.5 rounded-lg ${palettes[color]} ${pulse ? 'animate-pulse' : ''}`}>
        <Icon className="h-5 w-5" />
      </div>
      <div className="min-w-0">
        <p className="text-sm text-gray-500">{label}</p>
        <p className="text-lg font-semibold text-gray-900 truncate">{value}</p>
      </div>
      {to && <ArrowRight className="h-4 w-4 text-gray-300 ml-auto opacity-0 group-hover:opacity-100 transition-opacity" />}
    </div>
  )

  return to ? <Link to={to}>{inner}</Link> : inner
}

function QuickAction({ to, icon: Icon, color, label, desc }) {
  const palettes = {
    brand:   'bg-brand-50 text-brand-600 group-hover:bg-brand-100',
    emerald: 'bg-emerald-50 text-emerald-600 group-hover:bg-emerald-100',
    violet:  'bg-violet-50 text-violet-600 group-hover:bg-violet-100',
  }

  return (
    <Link
      to={to}
      className="card flex items-center gap-4 hover:shadow-md transition-shadow group"
    >
      <div className={`p-3 rounded-xl transition-colors ${palettes[color]}`}>
        <Icon className="h-5 w-5" />
      </div>
      <div>
        <p className="text-sm font-semibold text-gray-900">{label}</p>
        <p className="text-xs text-gray-500">{desc}</p>
      </div>
    </Link>
  )
}
